import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { api, formatUSD } from "../../../lib/api";
import { toast } from "sonner";
import { CurrencyDollar, Lightning, Warning } from "@phosphor-icons/react";

/**
 * BulkTopUpDialog — admin selects N agents from `selectedAgents` and assigns
 * them a USD float-balance amount each. A single Stripe Checkout is created
 * for the total; agents are credited on `checkout.session.completed`.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - selectedAgents: [{ agent_id, agent_code, full_name, email }]
 */
export default function BulkTopUpDialog({ open, onClose, selectedAgents }) {
  const [defaultAmount, setDefaultAmount] = useState("500");
  const [overrides, setOverrides] = useState({}); // { agent_id: "amount-string" }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state when the dialog opens/closes
  useEffect(() => {
    if (open) {
      setDefaultAmount("500");
      setOverrides({});
      setNote("");
    }
  }, [open]);

  const items = useMemo(() => {
    return selectedAgents.map(a => ({
      ...a,
      amount: Number((overrides[a.agent_id] ?? defaultAmount) || 0),
    }));
  }, [selectedAgents, overrides, defaultAmount]);

  const total = useMemo(
    () => items.reduce((s, it) => s + (Number.isFinite(it.amount) ? it.amount : 0), 0),
    [items],
  );

  const invalidCount = items.filter(it => !(it.amount > 0)).length;

  const submit = async () => {
    if (selectedAgents.length === 0) {
      toast.error("No agents selected.");
      return;
    }
    if (invalidCount > 0) {
      toast.error(`${invalidCount} agent${invalidCount === 1 ? "" : "s"} have an invalid amount.`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map(it => ({ agent_id: it.agent_id, amount_usd: it.amount })),
        origin_url: window.location.origin,
        note: note.trim(),
      };
      const r = await api.post("/admin/agents/bulk-topup-checkout", payload);
      const url = r.data?.checkout_url;
      if (!url) {
        toast.error("Stripe Checkout URL missing — please retry.");
        return;
      }
      // Redirect to Stripe Checkout — admin will pay then bounce back to /admin/agents.
      window.location.assign(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not initiate bulk top-up");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy && !v) onClose(); }}>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-2xl" data-testid="bulk-topup-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl text-[#184A30] flex items-center gap-2">
            <Lightning size={26} weight="fill" className="text-[#CBA35C]" /> Bulk top-up agents
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            Seed each agent&apos;s operating-float balance in one Stripe Checkout. Funds are credited only on successful payment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="bulk-topup-default" className="text-xs uppercase tracking-widest text-[#4A5C50]">Default amount per agent (USD)</Label>
          <div className="relative">
            <CurrencyDollar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
            <Input
              id="bulk-topup-default"
              type="number" min="1" step="1"
              value={defaultAmount}
              onChange={(e) => setDefaultAmount(e.target.value)}
              className="pl-8"
              disabled={busy}
              data-testid="bulk-topup-default-amount"
            />
          </div>
          <p className="text-[11px] text-[#4A5C50]">Override per-agent below if needed. Leave the override blank to use this default.</p>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-xl max-h-[260px] overflow-y-auto divide-y divide-[#E5E0D8]" data-testid="bulk-topup-agent-list">
          {selectedAgents.map(a => {
            const override = overrides[a.agent_id];
            const effective = Number((override ?? defaultAmount) || 0);
            const valid = effective > 0;
            return (
              <div key={a.agent_id} className="flex items-center gap-3 p-3 text-sm" data-testid={`bulk-topup-row-${a.agent_code}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#112318] truncate">{a.full_name || "—"}</p>
                  <p className="mono text-[10px] text-[#4A5C50]">{a.agent_code} · {a.email}</p>
                </div>
                <div className="relative w-32 shrink-0">
                  <CurrencyDollar size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
                  <Input
                    type="number" min="0" step="1"
                    placeholder={defaultAmount}
                    value={override ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOverrides(prev => {
                        const n = { ...prev };
                        if (val === "") delete n[a.agent_id];
                        else n[a.agent_id] = val;
                        return n;
                      });
                    }}
                    className={`pl-7 h-9 text-sm ${valid ? "" : "border-amber-400"}`}
                    disabled={busy}
                    data-testid={`bulk-topup-override-${a.agent_code}`}
                  />
                </div>
                <div className="w-20 text-right mono font-semibold text-[#112318] shrink-0">
                  {formatUSD(effective)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="bulk-topup-note" className="text-xs uppercase tracking-widest text-[#4A5C50]">Note (optional, for the audit log)</Label>
          <Input
            id="bulk-topup-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Q1 operating-float seed for active agents"
            disabled={busy}
            data-testid="bulk-topup-note"
          />
        </div>

        {invalidCount > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex gap-2" data-testid="bulk-topup-invalid-warning">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
            <div>
              <strong>{invalidCount}</strong> agent{invalidCount === 1 ? " has" : "s have"} an invalid amount (must be &gt; $0). Fix or remove them before continuing.
            </div>
          </div>
        )}

        <div className="bg-[#184A30] text-white rounded-xl p-4 flex items-baseline justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8BA888]">Total to charge</p>
            <p className="text-[10px] text-[#8BA888] mt-1">across {selectedAgents.length} agent{selectedAgents.length === 1 ? "" : "s"}</p>
          </div>
          <p className="serif text-3xl" data-testid="bulk-topup-total">{formatUSD(total)}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || total <= 0 || invalidCount > 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bulk-topup-submit-btn"
          >
            {busy ? "Redirecting to Stripe…" : `Continue to Stripe Checkout · ${formatUSD(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
